import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { QueryFunction } from '@tanstack/react-query';
import { ExtractedFieldResponse, ExtractedFieldType } from './types';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';

export interface GetExtractedDocumentDataParams {
  documentId: string;
}

interface GetExtractedDocumentDataResponse {
  success: boolean;
  status: string;
  extractedData: ExtractedFieldResponse[];
  nextCursor: string | null;
}

const getExtractedDocumentData: QueryFunction<
  GetExtractedDocumentDataResponse,
  ['/extracted-document-data', GetExtractedDocumentDataParams]
> = async ({ queryKey }) => {
  const { documentId } = queryKey[1];

  if (isMockServices()) return await mock(_mockExtractedData);

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<GetExtractedDocumentDataResponse>(
    `/extractedData/documents/${documentId}`,
    {
      method: 'GET',
      paginate: true,
      accumulateKey: 'extractedData',
      nextCursorKey: 'nextCursor',
      limit: 100,
    },
  );

  if (response.data) {
    return {
      success: true,
      status: response.data.status ?? 'success',
      extractedData: response.data.extractedData ?? [],
      nextCursor: response.data.nextCursor,
    };
  }

  throw new Error('Failed to fetch extracted document data');
};

const _mockExtractedData: GetExtractedDocumentDataResponse = {
  success: true,
  status: 'success',
  extractedData: [
    {
      id: 'field-1',
      documentId: 'doc-1',
      pageId: 'page-1',
      fieldName: 'borrowerName',
      fieldValue: 'John Doe',
      initialValue: 'John Doe',
      fieldConfidence: 95.5,
      boundingBoxes: {
        top: 100,
        left: 50,
        bottom: 120,
        right: 200,
      },
      manuallyCorrected: false,
      userApproved: false,
      notFound: false,
      correctedBy: null,
      validationResults: null,
      createdAt: new Date('2023-01-01T10:00:00Z'),
      updatedAt: new Date('2023-01-01T10:00:00Z'),
      isDeleted: false,
      document_id: 'doc-1',
      field_type_id: 'borrower-name-type',
      fieldType: 'text' as unknown as ExtractedFieldType,
      tableValue: null,
    },
    {
      id: 'field-2',
      documentId: 'doc-1',
      pageId: 'page-1',
      fieldName: 'loanAmount',
      fieldValue: '500000',
      initialValue: '500000',
      fieldConfidence: 88.2,
      boundingBoxes: {
        top: 150,
        left: 50,
        bottom: 170,
        right: 150,
      },
      manuallyCorrected: false,
      userApproved: false,
      notFound: false,
      correctedBy: null,
      validationResults: null,
      createdAt: new Date('2023-01-01T10:00:00Z'),
      updatedAt: new Date('2023-01-01T10:00:00Z'),
      isDeleted: false,
      document_id: 'doc-1',
      field_type_id: 'loan-amount-type',
      fieldType: 'number' as unknown as ExtractedFieldType,
      tableValue: null,
    },
  ],
  nextCursor: null,
};

export default getExtractedDocumentData;
